import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { testTelegramDestination, listDestinations, saveDestination, deleteDestination } from "@/lib/telegram.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Send, Pencil, Trash2, Eye, EyeOff, FlaskConical, CheckCircle2, XCircle } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";

export const Route = createFileRoute("/_authenticated/telegram")({ component: Page });

type Dest = {
  id: string;
  destination_name: string;
  destination_type: "account" | "group" | "channel";
  chat_id: string;
  status: boolean;
  last_sent_at: string | null;
  last_send_status: string | null;
  bot_token_masked: string;
  has_token: boolean;
};

type DestType = "account" | "group" | "channel";
const empty: { destination_name: string; destination_type: DestType; bot_token: string; chat_id: string; status: boolean } = { destination_name: "", destination_type: "group", bot_token: "", chat_id: "", status: true };

function Page() {
  const qc = useQueryClient();
  const test = useServerFn(testTelegramDestination);
  const listFn = useServerFn(listDestinations);
  const saveFn = useServerFn(saveDestination);
  const removeFn = useServerFn(deleteDestination);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Dest | null>(null);
  const [form, setForm] = useState<typeof empty>(empty);
  const [showToken, setShowToken] = useState(false);
  const [testDestId, setTestDestId] = useState<string>("");
  const [testMsg, setTestMsg] = useState<string>("");
  const [testResult, setTestResult] = useState<any>(null);
  const [testing, setTesting] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["telegram_destinations"],
    queryFn: async () => (await listFn()) as Dest[],
  });

  const { data: latestLogs } = useQuery({
    queryKey: ["telegram_destinations_latest_logs"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("telegram_send_logs")
        .select("id, telegram_destination_id, send_status, error_message, duration_ms, sent_at, attempt_no")
        .order("sent_at", { ascending: false })
        .limit(200);
      if (error) throw error;
      const map: Record<string, any> = {};
      for (const row of data ?? []) {
        const k = row.telegram_destination_id as string;
        if (k && !map[k]) map[k] = row;
      }
      return map;
    },
    refetchInterval: 5000,
  });

  const openNew = () => { setEditing(null); setForm(empty); setShowToken(false); setOpen(true); };
  const openEdit = (d: Dest) => {
    setEditing(d);
    // Bot token is never sent to the client. Leave blank — empty means "keep existing".
    setForm({ destination_name: d.destination_name, destination_type: d.destination_type, bot_token: "", chat_id: d.chat_id, status: d.status });
    setShowToken(false);
    setOpen(true);
  };

  const save = async () => {
    if (!form.destination_name.trim() || !form.chat_id.trim()) return toast.error("กรุณากรอกข้อมูลให้ครบ");
    if (!editing && !form.bot_token.trim()) return toast.error("ต้องระบุ Bot Token");
    try {
      await saveFn({
        data: {
          id: editing?.id,
          destination_name: form.destination_name.trim(),
          destination_type: form.destination_type,
          chat_id: form.chat_id.trim(),
          status: form.status,
          bot_token: form.bot_token.trim() || undefined,
        },
      });
      toast.success(editing ? "อัปเดตสำเร็จ" : "เพิ่มปลายทางสำเร็จ");
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["telegram_destinations"] });
    } catch (e: any) {
      toast.error(e.message ?? "บันทึกล้มเหลว");
    }
  };

  const remove = async (id: string) => {
    if (!confirm("ลบปลายทางนี้?")) return;
    try {
      await removeFn({ data: { id } });
      toast.success("ลบสำเร็จ");
      qc.invalidateQueries({ queryKey: ["telegram_destinations"] });
    } catch (e: any) { toast.error(e.message); }
  };

  const sendTest = async (id: string) => {
    try {
      toast.loading("กำลังส่งข้อความทดสอบ...", { id: "tg-test" });
      const r: any = await test({ data: { id } });
      if (!r?.ok) throw new Error(r?.error ?? "ส่งล้มเหลว");
      toast.success("ส่งข้อความทดสอบสำเร็จ ✅", { id: "tg-test" });
      qc.invalidateQueries({ queryKey: ["telegram_destinations"] });
      qc.invalidateQueries({ queryKey: ["telegram_destinations_latest_logs"] });
    } catch (e: any) {
      toast.error(`ส่งล้มเหลว: ${e.message}`, { id: "tg-test" });
      qc.invalidateQueries({ queryKey: ["telegram_destinations_latest_logs"] });
    }
  };

  const runTestSend = async () => {
    if (!testDestId) return toast.error("เลือกปลายทางก่อน");
    setTesting(true);
    setTestResult(null);
    try {
      const r: any = await test({ data: { id: testDestId, customMessage: testMsg.trim() || undefined } });
      setTestResult(r);
      qc.invalidateQueries({ queryKey: ["telegram_destinations"] });
      qc.invalidateQueries({ queryKey: ["telegram_destinations_latest_logs"] });
      if (r?.ok) toast.success("ส่งทดสอบสำเร็จ");
      else toast.error(`ส่งล้มเหลว: ${r?.error ?? "unknown"}`);
    } catch (e: any) {
      setTestResult({ ok: false, error: e.message ?? String(e) });
      toast.error(`ส่งล้มเหลว: ${e.message ?? e}`);
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Telegram Destinations</h1>
          <p className="text-sm text-muted-foreground">จัดการ Bot Token และ Chat/Group/Channel ID สำหรับส่งข่าว</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button onClick={openNew}><Plus className="h-4 w-4 mr-2"/>เพิ่มปลายทาง</Button></DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>{editing ? "แก้ไขปลายทาง" : "เพิ่มปลายทาง Telegram"}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>ชื่อปลายทาง</Label>
                <Input placeholder="เช่น กลุ่มข่าวทองคำ VIP" value={form.destination_name} onChange={(e) => setForm({ ...form, destination_name: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>ประเภท</Label>
                <Select value={form.destination_type} onValueChange={(v: any) => setForm({ ...form, destination_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="account">Account (ส่งหาบุคคล)</SelectItem>
                    <SelectItem value="group">Group (กลุ่ม)</SelectItem>
                    <SelectItem value="channel">Channel (ช่อง)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Bot Token</Label>
                <div className="flex gap-2">
                  <Input type={showToken ? "text" : "password"} placeholder={editing ? "เว้นว่าง = ใช้ Token เดิม" : "123456:ABC-DEF..."} value={form.bot_token} onChange={(e) => setForm({ ...form, bot_token: e.target.value })} />
                  <Button type="button" variant="outline" size="icon" onClick={() => setShowToken(!showToken)}>
                    {showToken ? <EyeOff className="h-4 w-4"/> : <Eye className="h-4 w-4"/>}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">ขอ Bot Token จาก @BotFather บน Telegram · Token จะถูกเข้ารหัสเก็บในฐานข้อมูล (AES-256-GCM)</p>
              </div>
              <div className="space-y-2">
                <Label>Chat ID / Group ID / Channel ID</Label>
                <Input placeholder="เช่น -1001234567890 หรือ @channelname" value={form.chat_id} onChange={(e) => setForm({ ...form, chat_id: e.target.value })} />
                <p className="text-xs text-muted-foreground">Group: ค่าจะเป็นเลขลบ เช่น -1001234567890 / Channel: ใช้ @username ได้</p>
              </div>
              <div className="flex items-center justify-between">
                <Label>เปิดใช้งาน</Label>
                <Switch checked={form.status} onCheckedChange={(v) => setForm({ ...form, status: v })} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>ยกเลิก</Button>
              <Button onClick={save}>บันทึก</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader><CardTitle>รายการปลายทาง</CardTitle><CardDescription>ทั้งหมด {data?.length ?? 0} รายการ</CardDescription></CardHeader>
        <CardContent>
          {isLoading ? <p className="text-sm text-muted-foreground">กำลังโหลด...</p> : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>ชื่อ</TableHead><TableHead>ประเภท</TableHead><TableHead>Chat ID</TableHead><TableHead>Bot Token</TableHead><TableHead>สถานะ</TableHead><TableHead>ผลลัพธ์ล่าสุด</TableHead><TableHead className="text-right">การกระทำ</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {data?.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">ยังไม่มีปลายทาง — กดปุ่ม "เพิ่มปลายทาง" ด้านบน</TableCell></TableRow>}
                {data?.map((d) => {
                  const log = latestLogs?.[d.id];
                  return (
                  <TableRow key={d.id}>
                    <TableCell className="font-medium">{d.destination_name}</TableCell>
                    <TableCell><Badge variant="outline">{d.destination_type}</Badge></TableCell>
                    <TableCell className="font-mono text-xs">{d.chat_id}</TableCell>
                    <TableCell className="font-mono text-xs">{d.has_token ? "••••••" : <span className="text-muted-foreground">— ไม่มี</span>}</TableCell>
                    <TableCell>{d.status ? <Badge>Active</Badge> : <Badge variant="secondary">Off</Badge>}</TableCell>
                    <TableCell className="text-xs">
                      {log ? (
                        <div className="space-y-0.5">
                          <div className="flex items-center gap-2">
                            {log.send_status === "success"
                              ? <Badge className="bg-green-600 hover:bg-green-600">success</Badge>
                              : <Badge variant="destructive">failed</Badge>}
                            <span className="text-muted-foreground">{log.duration_ms ?? "—"} ms</span>
                            {log.attempt_no > 1 && <span className="text-muted-foreground">· try {log.attempt_no}</span>}
                          </div>
                          <div className="text-muted-foreground">{new Date(log.sent_at).toLocaleString("th-TH")}</div>
                          {log.error_message && <div className="text-destructive truncate max-w-[260px]" title={log.error_message}>{log.error_message}</div>}
                        </div>
                      ) : d.last_sent_at ? (
                        <>
                          <div>{new Date(d.last_sent_at).toLocaleString("th-TH")}</div>
                          <div className={d.last_send_status === "success" ? "text-green-500" : "text-destructive"}>{d.last_send_status}</div>
                        </>
                      ) : <span className="text-muted-foreground">— ยังไม่เคยส่ง</span>}
                    </TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button size="sm" variant="outline" onClick={() => sendTest(d.id)}><Send className="h-3 w-3 mr-1"/>Test</Button>
                      <Button size="sm" variant="ghost" onClick={() => openEdit(d)}><Pencil className="h-3 w-3"/></Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(d.id)}><Trash2 className="h-3 w-3 text-destructive"/></Button>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><FlaskConical className="h-4 w-4 text-primary"/>Test Send</CardTitle>
          <CardDescription>ส่งข้อความตัวอย่างไปยังปลายทางที่เลือก พร้อมแสดงผลลัพธ์ละเอียด</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-2">
              <Label>ปลายทาง</Label>
              <Select value={testDestId} onValueChange={setTestDestId}>
                <SelectTrigger><SelectValue placeholder="เลือกปลายทาง..."/></SelectTrigger>
                <SelectContent>
                  {(data ?? []).map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.destination_name} · {d.destination_type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button onClick={runTestSend} disabled={testing || !testDestId} className="w-full md:w-auto">
                <Send className="h-4 w-4 mr-2"/>{testing ? "กำลังส่ง..." : "Send Test"}
              </Button>
            </div>
          </div>
          <div className="space-y-2">
            <Label>ข้อความ (เว้นว่างเพื่อใช้ข้อความเริ่มต้น)</Label>
            <Textarea
              rows={4}
              placeholder={"เช่น: 🧪 ทดสอบการเชื่อมต่อ Telegram จากระบบ AI Investment News"}
              value={testMsg}
              onChange={(e) => setTestMsg(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">รองรับ HTML tags เช่น &lt;b&gt;, &lt;i&gt;, &lt;a href=""&gt;</p>
          </div>

          {testResult && (
            <div className={`rounded-md border p-4 space-y-2 ${testResult.ok ? "border-green-600/30 bg-green-600/5" : "border-destructive/30 bg-destructive/5"}`}>
              <div className="flex items-center gap-2 font-medium">
                {testResult.ok
                  ? <><CheckCircle2 className="h-5 w-5 text-green-500"/><span className="text-green-500">ส่งสำเร็จ</span></>
                  : <><XCircle className="h-5 w-5 text-destructive"/><span className="text-destructive">ส่งล้มเหลว</span></>}
              </div>
              <div className="grid gap-2 md:grid-cols-2 text-xs">
                <div><span className="text-muted-foreground">ปลายทาง: </span>{testResult.destination_name ?? "—"}</div>
                <div><span className="text-muted-foreground">Chat ID: </span><span className="font-mono">{testResult.chat_id ?? "—"}</span></div>
                <div><span className="text-muted-foreground">HTTP Status: </span>{testResult.status ?? "—"}</div>
                <div><span className="text-muted-foreground">Duration: </span>{testResult.duration_ms ?? "—"} ms</div>
                {testResult.message_id && <div><span className="text-muted-foreground">Message ID: </span>{testResult.message_id}</div>}
              </div>
              {testResult.error && (
                <div className="text-xs">
                  <div className="text-muted-foreground mb-1">Error:</div>
                  <div className="font-mono text-destructive bg-background/50 p-2 rounded border border-destructive/20">{testResult.error}</div>
                </div>
              )}
              {testResult.response && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-muted-foreground hover:text-foreground">Raw response</summary>
                  <pre className="mt-2 p-2 bg-background/50 rounded border border-border overflow-auto max-h-60 text-[10px]">{JSON.stringify(testResult.response, null, 2)}</pre>
                </details>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-primary/20 bg-card/50">
        <CardHeader><CardTitle className="text-base">วิธีใช้งาน</CardTitle></CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p>1. สร้าง Bot ที่ <span className="font-mono text-foreground">@BotFather</span> บน Telegram → รับ Bot Token</p>
          <p>2. เพิ่ม Bot เข้ากลุ่ม/ช่องที่ต้องการ และให้สิทธิ์ส่งข้อความ</p>
          <p>3. หา Chat ID: ส่งข้อความในกลุ่ม → เปิด <span className="font-mono text-foreground">https://api.telegram.org/bot[TOKEN]/getUpdates</span></p>
          <p>4. กด <span className="text-foreground font-medium">Test</span> เพื่อทดสอบการเชื่อมต่อก่อนใช้งานจริง</p>
        </CardContent>
      </Card>
    </div>
  );
}
